import React, { useCallback, useState } from 'react';
import ConversationsDataTable from './ConversationsDataTable';
import { getApiBase } from '../../lib/apiBase';
import { getDashboardAuthHeaders } from '../../lib/dashboardSession';
import { normalizeDashboardMessages } from '../../lib/dashboardMessages';
import { openTranscriptPrintWindow } from '../../utils/transcriptPdf';

const MIN_MESSAGE_FILTERS = [
  { value: 0, label: 'Show all conversations' },
  { value: 3, label: 'Hide under 3 messages' },
  { value: 5, label: 'Hide under 5 messages' },
  { value: 6, label: 'Only more than 5 messages' },
  { value: 11, label: 'Only more than 10 messages' },
];

const MIN_MESSAGES_KEY = 'berwind-dashboard-min-messages';

export default function ConversationsView({ onError, refreshToken }) {
  const [selected, setSelected] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [clearToken, setClearToken] = useState(0);
  // Remember the admin's choice so the list stays filtered across visits.
  const [minMessages, setMinMessages] = useState(() => {
    try {
      const saved = window.localStorage.getItem(MIN_MESSAGES_KEY);
      if (saved != null && saved !== '') {
        const n = parseInt(saved, 10);
        if (Number.isFinite(n) && n >= 0) return n;
      }
    } catch (_) {}
    return 6;
  });

  const handleMinMessagesChange = (value) => {
    const n = Number(value);
    setMinMessages(n);
    try {
      window.localStorage.setItem(MIN_MESSAGES_KEY, String(n));
    } catch (_) {}
  };

  const handleSelectionChange = useCallback((rows) => {
    setSelected(Array.isArray(rows) ? rows : []);
  }, []);

  const handleExport = async () => {
    if (!selected.length || exporting) return;
    setExporting(true);
    onError('');
    try {
      const headers = getDashboardAuthHeaders();

      // Reviews (name + stars) keyed by session, for the transcript header block.
      const feedbackBySession = {};
      try {
        const res = await fetch(`${getApiBase()}/api/dashboard/feedback?limit=500`, { headers });
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data.feedback)) {
          data.feedback.forEach((f) => {
            const sid = f.sessionId || f.session_id;
            if (sid && !feedbackBySession[sid]) feedbackBySession[sid] = f;
          });
        }
      } catch (_) {
        /* reviews are optional — a transcript without one just omits the box */
      }

      // Newest first, matching the table's default ordering.
      const ordered = [...selected].sort(
        (a, b) => new Date(b.startedAt || 0) - new Date(a.startedAt || 0)
      );

      const items = [];
      for (const session of ordered) {
        let messages = normalizeDashboardMessages(session.messages);
        if (!messages.length && session.sessionId) {
          // eslint-disable-next-line no-await-in-loop
          const res = await fetch(
            `${getApiBase()}/api/dashboard/session-messages?sessionId=${encodeURIComponent(session.sessionId)}`,
            { headers }
          );
          // eslint-disable-next-line no-await-in-loop
          const data = await res.json().catch(() => ({}));
          if (res.ok) messages = normalizeDashboardMessages(data.messages);
        }
        items.push({
          session,
          messages,
          feedback: feedbackBySession[session.sessionId] || null,
        });
      }

      const opened = openTranscriptPrintWindow(items);
      if (!opened) {
        onError('Pop-up blocked. Allow pop-ups for this site, then try the download again.');
      }
    } catch (err) {
      onError(err?.message || 'Failed to build the transcript PDF');
    } finally {
      setExporting(false);
    }
  };

  const clearSelection = () => {
    setSelected([]);
    setClearToken((n) => n + 1);
  };

  return (
    <>
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
          <h3 className="card-title mb-0">
            <i className="fas fa-table mr-1" />
            Conversations
          </h3>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            <select
              className="form-control form-control-sm"
              style={{ width: 'auto' }}
              value={minMessages}
              onChange={(e) => handleMinMessagesChange(e.target.value)}
              title="Filter which conversations appear here. Nothing is deleted — hidden sessions stay in the database."
              aria-label="Filter conversations by message count"
            >
              {MIN_MESSAGE_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {selected.length > 0 && (
              <>
                <span className="text-muted small">{selected.length} selected</span>
                <button
                  type="button"
                  className="btn btn-sm btn-default"
                  onClick={clearSelection}
                  disabled={exporting}
                >
                  Clear
                </button>
              </>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleExport}
              disabled={!selected.length || exporting}
              title={
                selected.length
                  ? 'Download the selected conversations as a PDF transcript'
                  : 'Tick one or more conversations to download'
              }
            >
              <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-file-pdf'} mr-1`} />
              {exporting
                ? 'Preparing…'
                : `Download PDF${selected.length ? ` (${selected.length})` : ''}`}
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          <ConversationsDataTable
            refreshToken={refreshToken}
            onError={onError}
            onSelectionChange={handleSelectionChange}
            clearSelectionToken={clearToken}
            minMessages={minMessages}
          />
        </div>
      </div>
    </>
  );
}
